import { supabase, SUPABASE_URL } from '../supabaseClient';
import {
  SLIDE_IMAGE_STORAGE_LIMIT_BYTES,
  notifySlideImageStorageChanged,
  type SlideImageStorageUsage,
} from './slideImageStorageService';

const BUCKET = 'learning-worksheet-images';
const CACHE_CONTROL = '31536000';

function genStorageFileName(mimeType: string): string {
  const ext = mimeType === 'image/jpeg' ? 'jpg' : mimeType === 'image/png' ? 'png' : 'webp';
  const id = globalThis.crypto?.randomUUID?.();
  if (id) return `${id}.${ext}`;
  return `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
}

export function buildLearningWorksheetImageStoragePath(params: {
  userId: string;
  workspaceId: string;
  fileName: string;
}): string {
  const ws = (params.workspaceId ?? 'default').trim() || 'default';
  return `${params.userId}/worksheet/${ws}/${params.fileName}`;
}

export function isLearningWorksheetImageStoragePublicUrl(publicUrl: string): boolean {
  if (!publicUrl.trim() || !SUPABASE_URL) return false;
  const prefix = `${SUPABASE_URL}/storage/v1/object/public/${BUCKET}/`;
  return publicUrl.trim().startsWith(prefix);
}

export function storagePathFromLearningWorksheetImagePublicUrl(
  publicUrl: string,
  userId: string
): string | null {
  if (!publicUrl.trim() || !SUPABASE_URL) return null;
  const prefix = `${SUPABASE_URL}/storage/v1/object/public/${BUCKET}/`;
  if (!publicUrl.startsWith(prefix)) return null;
  const path = decodeURIComponent(publicUrl.slice(prefix.length).split('?')[0] ?? '');
  if (!path.startsWith(`${userId}/`)) return null;
  return path;
}

export async function fetchLearningWorksheetImageStorageUsage(): Promise<SlideImageStorageUsage | null> {
  const { data: userData, error: userErr } = await supabase.auth.getUser();
  if (userErr || !userData.user) return null;
  const { data, error } = await supabase.rpc('get_user_image_storage_usage');
  if (error) {
    const fallback = await supabase.rpc('get_slide_image_storage_usage');
    if (fallback.error) throw fallback.error;
    const row = Array.isArray(fallback.data) ? fallback.data[0] : fallback.data;
    return {
      bytesUsed: Number((row as { bytes_used?: number })?.bytes_used ?? 0),
      bytesLimit: Number((row as { bytes_limit?: number })?.bytes_limit ?? SLIDE_IMAGE_STORAGE_LIMIT_BYTES),
    };
  }
  const row = Array.isArray(data) ? data[0] : data;
  return {
    bytesUsed: Number((row as { bytes_used?: number })?.bytes_used ?? 0),
    bytesLimit: Number((row as { bytes_limit?: number })?.bytes_limit ?? SLIDE_IMAGE_STORAGE_LIMIT_BYTES),
  };
}

export async function uploadLearningWorksheetImage(params: {
  blob: Blob;
  mimeType: string;
  workspaceId: string;
}): Promise<{ publicUrl: string; storagePath: string }> {
  const { blob, mimeType, workspaceId } = params;
  const { data: userData, error: userErr } = await supabase.auth.getUser();
  const userId = userData.user?.id?.trim();
  if (userErr || !userId) throw new Error('로그인이 필요합니다.');
  const ws = workspaceId.trim();
  if (!ws) throw new Error('workspaceId required');

  const usage = await fetchLearningWorksheetImageStorageUsage();
  if (usage && usage.bytesUsed + blob.size > usage.bytesLimit) {
    throw new Error('QUOTA_EXCEEDED');
  }

  const fileName = genStorageFileName(mimeType);
  const storagePath = buildLearningWorksheetImageStoragePath({ userId, workspaceId: ws, fileName });

  const { error: uploadErr } = await supabase.storage.from(BUCKET).upload(storagePath, blob, {
    contentType: mimeType,
    cacheControl: CACHE_CONTROL,
    upsert: false,
  });
  if (uploadErr) throw uploadErr;

  try {
    const { error: regErr } = await supabase.rpc('register_learning_worksheet_image_asset', {
      p_path: storagePath,
      p_bytes: blob.size,
      p_workspace_id: ws,
    });
    if (regErr) {
      await supabase.storage.from(BUCKET).remove([storagePath]);
      if (String(regErr.message ?? '').includes('QUOTA_EXCEEDED')) {
        throw new Error('QUOTA_EXCEEDED');
      }
      throw regErr;
    }
  } catch (e) {
    await supabase.storage.from(BUCKET).remove([storagePath]);
    throw e;
  }

  const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(storagePath);
  const publicUrl = urlData.publicUrl;
  if (!publicUrl) throw new Error('이미지 URL을 만들지 못했습니다.');

  notifySlideImageStorageChanged();
  return { publicUrl, storagePath };
}

export async function deleteLearningWorksheetImageByPublicUrl(publicUrl: string): Promise<void> {
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData.user?.id?.trim();
  if (!userId || !publicUrl.trim()) return;
  const path = storagePathFromLearningWorksheetImagePublicUrl(publicUrl, userId);
  if (!path) return;
  await supabase.rpc('release_learning_worksheet_image_asset', { p_path: path });
  await supabase.storage.from(BUCKET).remove([path]);
  notifySlideImageStorageChanged();
}
